import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { Button } from '@xing-com/crate-lebenslauf-button';
import { JobSearchByQueryVitaDocument } from '@xing-com/crate-lebenslauf-graphql';
import {
  useHostName,
  useIsCoverLetterEditor,
  useReadVitaReactiveVariable,
  useVitaTracking,
} from '@xing-com/crate-lebenslauf-hooks';
import { NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';
import { IconArrowRight, IconNotificationCenter } from '@xing-com/icons';
import { BodyCopy, Headline } from '@xing-com/typography';

import {
  StyledButtonWrapper,
  StyledContinueButton,
  StyledEmptySearch,
  StyledHero,
  StyledIllustration,
  StyledJobTeaserCard,
  StyledJobsWindow,
  StyledMoreButton,
  StyledScrollWrapper,
} from './jobs.styled';

const JobsInner: FC<JobsProps> = ({ togglePreferences, onClick }) => {
  const { isLoggedOut } = useLoginState();
  const intl = useIntl();
  const hostName = useHostName();
  const { getTrackingPrefixes, trackWithNWT } = useVitaTracking({
    pageName: 'editor',
  });
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState('');
  const { cv } = useReadVitaReactiveVariable();
  const locations = cv?.preferences?.locations?.length
    ? cv.preferences.locations.map(
        (location) => location?.cityIdWithScrambling || ''
      )
    : [];
  const query = {
    keywords: cv?.preferences?.targetPosition,
    filter: {
      city: {
        id: locations,
      },
      remoteOption: cv?.preferences?.homeOffice
        ? {
            id: ['FULL_REMOTE.050e26'],
          }
        : undefined,
    },
  };
  const { data } = useSuspenseQuery(JobSearchByQueryVitaDocument, {
    variables: {
      query,
      consumer: 'loggedout.web.lebenslauf.jobs',
      limit: 6,
    },
    errorPolicy: 'all',
    ssr: false,
  });
  const isCoverLetterEditor = useIsCoverLetterEditor();
  const jobSearchByQuery = data?.jobSearchByQuery;
  const searchParams = `city=${locations.join('*')}&keywords=${
    cv?.preferences?.targetPosition
  }${cv?.preferences?.homeOffice ? '&remoteOption=FULL_REMOTE.050e26' : ''}`;
  const state =
    jobSearchByQuery && jobSearchByQuery.total > 6
      ? 'jobs_more_than_6'
      : jobSearchByQuery && jobSearchByQuery.total < 3
        ? 'jobs_less_than_3'
        : 'jobs_3_to_6';
  const showJobsWindow = (): void => {
    setShowPopup(true);
    setPopupContent('jobs');
  };
  const showAlertWindow = (): void => {
    setShowPopup(true);
    setPopupContent('alert');
  };
  const handleOpenPreferences = (): void => {
    togglePreferences();
  };
  const closeWindow = (): void => {
    setShowPopup(false);
    setTimeout(() => {
      setPopupContent('');
    }, 500);
  };

  const isAnschreibenHost = hostName.includes('anschreiben');
  const isCvInAnschreiben = !isCoverLetterEditor && isAnschreibenHost;
  const trackingPrefix = isCvInAnschreiben
    ? getTrackingPrefixes().cv
    : getTrackingPrefixes().host;

  const handleOnClick = (): void => {
    if (cv?.preferences?.targetPosition && jobSearchByQuery?.total) {
      showJobsWindow();
    } else {
      if (!cv?.preferences?.targetPosition) {
        handleOpenPreferences();
      } else {
        showAlertWindow();
      }
    }
    if (onClick) {
      onClick();
    }
  };

  const handleTrackingJobAd = (
    state: string,
    index: number,
    urn: string,
    isExternalJob: boolean
  ): void => {
    const flagsArray: string[] = [];

    if (!isCvInAnschreiben) {
      flagsArray.push('anschreiben_ai_true');
    }

    if (isExternalJob) {
      flagsArray.push('external_job_ad');
    }

    const flags = flagsArray.join(',');

    trackWithNWT({
      event: 'opened',
      element: 'job_posting',
      elementDetail: `job_recommendation_popup_${state}`,
      position: index,
      ...(!!flags && { flags }),
      itemUrn: urn,
    });
  };

  const handleTrackingShowMoreJobs = (state: string): void => {
    trackWithNWT({
      event: 'opened',
      element: 'show_more',
      elementDetail: `job_recommendation_popup_${state}`,
      ...(!isCvInAnschreiben && { flags: 'anschreiben_ai_true' }),
    });
  };

  const handleTrackingContinueButton = (): void => {
    trackWithNWT({
      event: 'clicked',
      element: 'continue_editing',
      elementDetail: `job_recommendation_popup_${state}`,
      ...(!isCvInAnschreiben && { flags: 'anschreiben_ai_true' }),
    });
  };

  return (
    <>
      {!isLoggedOut && <div />}
      {isLoggedOut && (
        <Button
          variant={isCoverLetterEditor ? 'secondary' : 'primary'}
          size={isCoverLetterEditor ? 'small' : 'medium'}
          onClick={handleOnClick}
          data-qa="lebenslauf-jobs-button"
        >
          {jobSearchByQuery?.total && cv?.preferences?.targetPosition ? (
            <FormattedMessage
              id="CRATE_LEBENSLAUF_JOBS_DISCOVER_BUTTON"
              values={{
                count: jobSearchByQuery?.total,
              }}
            />
          ) : (
            <FormattedMessage id="CRATE_LEBENSLAUF_JOBS_FIND_BUTTON" />
          )}
        </Button>
      )}

      <StyledJobsWindow
        isShown={showPopup}
        hide={closeWindow}
        onDimmerClick={closeWindow}
        $isWide={popupContent === 'jobs'}
      >
        {popupContent === 'jobs' && (
          <>
            <StyledHero size="small">
              <FormattedMessage id="CRATE_LEBENSLAUF_JOBS_HEADLINE" />
            </StyledHero>
            <BodyCopy>
              <FormattedMessage
                id="CRATE_LEBENSLAUF_JOBS_SUBLINE"
                values={{
                  count: jobSearchByQuery?.total,
                }}
              />
            </BodyCopy>
            <StyledScrollWrapper>
              {data?.jobSearchByQuery?.collection.map((job, index) => {
                if (job?.jobDetail?.__typename === 'VisibleJob' && index < 5) {
                  const jobDetailUrl = job.jobDetail.url;
                  const jobCode = !isAnschreibenHost ? '53' : '60';
                  const url = `${jobDetailUrl}?sc_o=${trackingPrefix}_editor_job_recommendation_popup_job_ad_click&sc_o_PropActionOrigin=${trackingPrefix}_editor_job_recommendation_popup_${state}&ijt=jb_${jobCode}`;
                  const urn = job.jobDetail?.globalId;
                  const isExternalJob = job.jobDetail.redirectsToThirdPartyUrl;
                  return (
                    <StyledJobTeaserCard
                      target="_blank"
                      key={index}
                      job={job.jobDetail}
                      onClick={() =>
                        handleTrackingJobAd(state, index, urn, isExternalJob)
                      }
                      url={url}
                    />
                  );
                }
                if (index === 5) {
                  return (
                    <StyledMoreButton
                      key={index}
                      icon={IconArrowRight}
                      iconAfter
                      href={`https://www.xing.com/jobs/search?${searchParams}&sc_o=${trackingPrefix}_editor_job_recommendation_popup_show_more_click&sc_o_PropActionOrigin=${trackingPrefix}_editor_job_recommendation_popup_${state}`}
                      size="medium"
                      sizeWide="large"
                      // @ts-expect-error FIXME: SC6
                      target="_blank"
                      onClick={() => handleTrackingShowMoreJobs(state)}
                    >
                      <FormattedMessage id="CRATE_LEBENSLAUF_JOBS_SHOW_MORE" />
                    </StyledMoreButton>
                  );
                }
                return null;
              })}
            </StyledScrollWrapper>
            <StyledButtonWrapper>
              <StyledContinueButton
                variant="primary"
                onClick={() => {
                  handleTrackingContinueButton();
                  closeWindow();
                }}
                size="medium"
              >
                <FormattedMessage id="CRATE_LEBENSLAUF_JOBS_BACK_TO_EDITOR" />
              </StyledContinueButton>
            </StyledButtonWrapper>
          </>
        )}
        {popupContent === 'alert' && (
          <StyledEmptySearch>
            <StyledIllustration size="large" />
            <Headline size="xxxlarge">
              <FormattedMessage
                id="CRATE_LEBENSLAUF_JOBS_EMPTY_HEADLINE"
                values={{
                  role: cv?.preferences?.targetPosition,
                  location: cv?.preferences?.locations?.length
                    ? cv.preferences.locations[0]?.cityName
                    : intl.formatMessage({
                        id: 'CRATE_LEBENSLAUF_JOBS_HOME_OFFICE',
                      }),
                }}
              />
            </Headline>
            <BodyCopy size="xlarge">
              <FormattedMessage id="CRATE_LEBENSLAUF_JOBS_EMPTY_SUBLINE" />
            </BodyCopy>
            <StyledContinueButton
              icon={IconNotificationCenter}
              variant="tertiary"
              href="https://www.xing.com/jobs/search"
              // @ts-expect-error FIXME: SC6
              target="_blank"
              size="medium"
            >
              <FormattedMessage id="CRATE_LEBENSLAUF_JOBS_FIND_MORE" />
            </StyledContinueButton>
          </StyledEmptySearch>
        )}
      </StyledJobsWindow>
    </>
  );
};

const FallbackButton: FC = () => {
  const isCoverLetterEditor = useIsCoverLetterEditor();

  return (
    <Button
      variant={isCoverLetterEditor ? 'secondary' : 'primary'}
      size={isCoverLetterEditor ? 'small' : 'medium'}
      loading
      data-qa="lebenslauf-jobs-button"
    >
      <FormattedMessage id="CRATE_LEBENSLAUF_JOBS_FIND_BUTTON" />
    </Button>
  );
};

type JobsProps = {
  togglePreferences: () => void;
  onClick?: () => void;
};

export const Jobs: FC<JobsProps> = (props) => {
  return (
    <NoSSRLoggedInSuspense fallback={<FallbackButton />}>
      <JobsInner {...props} />
    </NoSSRLoggedInSuspense>
  );
};
